import React, { useCallback, useEffect, useState } from "react";
import { fmtDate } from "@/lib/dates";
import { api, apiErr } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CareHomeProfileForm, LogoBox } from "@/components/CareHomeProfileForm";
import { toast } from "sonner";
import { Users, LogIn, GraduationCap, Award, Search, Download, Trash2 } from "lucide-react";

const fmt = (iso) => fmtDate(iso);
const STATUS_TONE = { active: "bg-rag-green-bg text-rag-green", trial: "bg-coral/10 text-coral", founding: "bg-brand-light text-brand", cancelled: "bg-rag-red-bg text-rag-red", past_due: "bg-rag-red-bg text-rag-red" };

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl bg-cream border border-cream-deep p-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className="text-xl font-heading font-bold text-brand mt-1">{value}</div>
    </div>
  );
}

function address(h) {
  return [h.address_line1, h.address_line2, h.town, h.postcode].filter(Boolean).join(", ") || "—";
}

export default function CareHomesAdmin() {
  const [homes, setHomes] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [exporting, setExporting] = useState(false);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/care-homes/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = "kohc-care-homes.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  const load = useCallback(() => api.get("/admin/care-homes").then((r) => setHomes(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);

  const openHome = async (h) => {
    setSelected(h); setDetail(null);
    try { const { data } = await api.get(`/admin/care-homes/${h.id}`); setDetail(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const refreshDetail = () => { if (selected) { openHome(selected); load(); } };
  const deleteHome = async () => {
    if (!selected) return;
    const typed = window.prompt(`This permanently deletes "${selected.name}", its manager, staff, enrolments, certificates and audit records.\n\nType the care home name to confirm:`);
    if (typed !== selected.name) { if (typed !== null) toast.error("Name didn't match — nothing deleted"); return; }
    try { await api.delete(`/admin/care-homes/${selected.id}`); toast.success(`${selected.name} deleted`); setSelected(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const filtered = homes.filter((h) => {
    const s = q.toLowerCase();
    return !s || [h.name, h.manager_name, h.contact_email, h.town, h.postcode].some((v) => (v || "").toLowerCase().includes(s));
  });
  const totals = homes.reduce((a, h) => ({ staff: a.staff + h.staff_count, logged: a.logged + h.staff_logged_in, done: a.done + h.staff_completed, champs: a.champs + (h.champion ? 1 : 0) }), { staff: 0, logged: 0, done: 0, champs: 0 });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat icon={Users} label="Care homes" value={homes.length} testid="ch-total-homes" />
        <Stat icon={Users} label="Staff registered" value={totals.staff} testid="ch-total-staff" />
        <Stat icon={LogIn} label="Staff logged in" value={totals.logged} testid="ch-total-logged" />
        <Stat icon={GraduationCap} label="Completed training" value={totals.done} testid="ch-total-completed" />
        <Stat icon={Award} label="Oral Champions" value={totals.champs} testid="ch-total-champions" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="ch-search" placeholder="Search by home, manager, email, town…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Button variant="outline" size="sm" data-testid="ch-export-csv-btn" disabled={exporting} onClick={exportCsv} className="rounded-full"><Download className="h-4 w-4 mr-1" />{exporting ? "Preparing…" : "Download CSV"}</Button>
      </div>

      <div className="bg-cream-card border border-cream-deep rounded-xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Care home</TableHead><TableHead>Manager</TableHead><TableHead>Contact</TableHead><TableHead>Beds</TableHead><TableHead>Plan</TableHead>
            <TableHead className="text-center">Staff</TableHead><TableHead className="text-center">Logged in</TableHead><TableHead className="text-center">Completed</TableHead><TableHead>Oral Champion</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((h, i) => (
              <TableRow key={h.id} data-testid={`ch-row-${i}`} onClick={() => openHome(h)} className={`cursor-pointer ${i % 2 ? "bg-cream/50" : ""}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <LogoBox url={h.logo_url} size="h-10 w-10" />
                    <div><div className="font-medium text-brand">{h.name}</div><div className="text-xs text-muted-foreground">{address(h)}</div></div>
                  </div>
                </TableCell>
                <TableCell><div className="text-sm">{h.manager_name || "—"}</div><div className="text-xs text-muted-foreground">Last login {fmt(h.manager_last_login_at)}</div></TableCell>
                <TableCell><div className="text-sm">{h.contact_email}</div><div className="text-xs text-muted-foreground">{h.phone || "—"}</div></TableCell>
                <TableCell>{h.beds ?? "—"}</TableCell>
                <TableCell><Badge className={`capitalize ${STATUS_TONE[h.subscription_status] || ""}`}>{h.subscription_status || "—"}</Badge></TableCell>
                <TableCell className="text-center font-medium">{h.staff_count}</TableCell>
                <TableCell className="text-center font-medium">{h.staff_logged_in}</TableCell>
                <TableCell className="text-center font-medium">{h.staff_completed}<span className="text-muted-foreground">/{h.staff_count}</span></TableCell>
                <TableCell>{h.champion ? <div><div className="text-sm">{h.champion.name}</div><Badge variant="outline" className="capitalize text-xs">{h.champion.certified ? "certified" : h.champion.status}</Badge></div> : <span className="text-xs text-muted-foreground">None yet</span>}</TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">No care homes found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Sheet open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <SheetContent data-testid="ch-detail-sheet" className="w-full sm:max-w-2xl overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-4">
                  <LogoBox url={detail?.logo_url ?? selected.logo_url} size="h-16 w-16" />
                  <div>
                    <SheetTitle className="font-heading text-brand">{detail?.name ?? selected.name}</SheetTitle>
                    <SheetDescription>{address(detail || selected)} · Joined {fmt(selected.created_at)}</SheetDescription>
                  </div>
                </div>
              </SheetHeader>
              {!detail ? <p className="text-sm text-muted-foreground mt-6">Loading…</p> : (
                <Tabs defaultValue="overview" className="mt-5">
                  <TabsList><TabsTrigger value="overview" data-testid="ch-tab-overview">Overview</TabsTrigger><TabsTrigger value="staff" data-testid="ch-tab-staff">Staff ({detail.staff.length})</TabsTrigger><TabsTrigger value="edit" data-testid="ch-tab-edit">Edit profile</TabsTrigger></TabsList>
                  <TabsContent value="overview" className="space-y-5">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <Stat icon={Users} label="Staff" value={detail.staff_count} testid="ch-d-staff" />
                      <Stat icon={LogIn} label="Logged in" value={detail.staff_logged_in} testid="ch-d-logged" />
                      <Stat icon={GraduationCap} label="Completed" value={detail.staff_completed} testid="ch-d-completed" />
                      <Stat icon={Award} label="Champions" value={detail.champion_count} testid="ch-d-champions" />
                    </div>
                    <div className="rounded-xl border border-cream-deep p-4 text-sm grid md:grid-cols-2 gap-x-6 gap-y-2">
                      <div><span className="text-muted-foreground">Manager</span><div className="font-medium">{detail.manager_name || "—"}</div></div>
                      <div><span className="text-muted-foreground">Email</span><div className="font-medium break-all">{detail.contact_email || "—"}</div></div>
                      <div><span className="text-muted-foreground">Telephone</span><div className="font-medium">{detail.phone || "—"}</div></div>
                      <div><span className="text-muted-foreground">Registered beds</span><div className="font-medium">{detail.beds ?? "—"}</div></div>
                      <div><span className="text-muted-foreground">Plan</span><div className="font-medium capitalize">{detail.subscription_status} {detail.plan ? `· ${detail.plan}` : ""}</div></div>
                      <div><span className="text-muted-foreground">{detail.subscription_status === "trial" ? "Trial ends" : "Renews"}</span><div className="font-medium">{fmt(detail.subscription_status === "trial" ? detail.trial_ends_at : detail.renewal_date)}</div></div>
                    </div>
                    <div>
                      <h4 className="font-heading font-bold text-brand mb-2">Oral Champion</h4>
                      {detail.champions.length === 0 ? <p className="text-sm text-muted-foreground">No Oral Champion registered yet.</p> : detail.champions.map((c, i) => (
                        <div key={`${c.email}-${i}`} data-testid={`ch-champion-${i}`} className="flex items-center justify-between text-sm py-2 border-b border-cream-deep last:border-0">
                          <div><div className="font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{c.email}{c.job_title ? ` · ${c.job_title}` : ""} · {c.session_date || "date TBC"}</div></div>
                          <Badge variant="outline" className="capitalize">{c.certified ? "Attended · certified" : c.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                  <TabsContent value="staff">
                    <div className="space-y-2">
                      {detail.staff.length === 0 && <p className="text-sm text-muted-foreground">No staff added yet.</p>}
                      {detail.staff.map((s, i) => (
                        <div key={s.id} data-testid={`ch-staff-${i}`} className="rounded-xl border border-cream-deep p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div><div className="font-medium text-brand">{s.name}</div><div className="text-xs text-muted-foreground">{s.email} · {s.job_role}</div></div>
                            <div className="text-right text-xs text-muted-foreground">
                              <div>{s.last_login_at ? `Last login ${fmt(s.last_login_at)} (${s.login_count}×)` : "Never logged in"}</div>
                              <div>Added {fmt(s.created_at)}</div>
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
                            {s.essential ? (
                              <div className="flex-1 min-w-[180px]">
                                <div className="flex justify-between mb-1"><span>Essential Oral Care Training</span><span className="font-medium">{s.essential.status === "completed" ? `Completed ${fmt(s.essential.completed_at)}` : `${s.essential.progress}%`}</span></div>
                                <div className="h-1.5 rounded-full bg-cream-deep overflow-hidden"><div className={`h-full ${s.essential.status === "completed" ? "bg-rag-green" : "bg-coral"}`} style={{ width: `${s.essential.progress}%` }} /></div>
                              </div>
                            ) : <span className="text-muted-foreground">Essential Oral Care Training not assigned</span>}
                            {s.certificates.length > 0 && <Badge className="bg-rag-green-bg text-rag-green"><Award className="h-3 w-3 mr-1" />{s.certificates.length} certificate{s.certificates.length > 1 ? "s" : ""}</Badge>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                  <TabsContent value="edit">
                    <CareHomeProfileForm getUrl={`/admin/care-homes/${selected.id}`} putUrl={`/admin/care-homes/${selected.id}`} logoUrl={`/admin/care-homes/${selected.id}/logo`} allowRemoveLogo={false} onSaved={refreshDetail} />
                  </TabsContent>
                </Tabs>
              )}
              {selected && !detail && null}
              <div className="mt-6 flex items-center justify-between gap-2">
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => setSelected(null)}>Close</Button>
                <Button variant="ghost" size="sm" data-testid="ch-delete-home-btn" onClick={deleteHome} className="rounded-full text-rag-red hover:bg-rag-red-bg"><Trash2 className="h-4 w-4 mr-1" />Delete care home</Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
